// Avisos: lo que el sistema le mando a este usuario.
//
// Administracion recibe "pedido web nuevo"; el local, "tu pedido quedo
// confirmado". Antes esos avisos solo existian como un numerito en la barra y
// como notificacion del celular: si el usuario no la veia en el momento, se
// perdia. Aca quedan todos.
//
// Al abrirla se marcan como leidos: entrar a la pantalla ES haberlos visto.

import { api } from "../api";
import { navegar } from "../router";
import {
  refrescarContador,
  alLlegarAviso,
  botonAvisosHtml,
  engancharBotonAvisos,
} from "../notificaciones";
import { iconoCampana, iconoFlechaDer, iconoTacho } from "../iconos";
import type { AvisosRespuesta, Aviso } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

/**
 * 'YYYY-MM-DD HH:MM:SS' en UTC -> hora de Montevideo, en formato corto.
 * La base guarda UTC (datetime('now')); el celular tiene que ver su hora.
 */
function cuando(utc: string): string {
  const d = new Date(utc.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return utc;
  return d.toLocaleString("es-UY", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fila(a: Aviso): string {
  const sinLeer = a.leida === 0;
  // Los avisos con hash llevan a la pantalla que los origino (Mis pedidos,
  // Pedidos web). Los que no tienen, son solo informativos.
  const etiqueta = a.hash ? "button" : "div";
  return `
    <${etiqueta} ${a.hash ? `data-ir="${esc(a.hash)}"` : ""}
      class="tarjeta flex w-full items-start gap-3 text-left ${a.hash ? "hover:bg-crema-50 active:bg-crema-100" : ""}">
      <span class="mt-0.5 shrink-0 ${sinLeer ? "text-acento-600" : "text-masa-300"}">${iconoCampana}</span>
      <span class="min-w-0 flex-1">
        <span class="block font-bold ${sinLeer ? "text-masa-900" : "text-masa-600"}">${esc(a.titulo)}</span>
        <span class="block text-sm text-masa-600">${esc(a.cuerpo)}</span>
        <span class="mt-1 block text-xs text-masa-400">${esc(cuando(a.creada_en))}</span>
      </span>
      ${a.hash ? `<span class="mt-0.5 shrink-0 text-masa-300">${iconoFlechaDer}</span>` : ""}
    </${etiqueta}>`;
}

export async function renderAvisos(contenedor: HTMLElement): Promise<void> {
  contenedor.innerHTML = `
    <div class="mb-4 flex flex-wrap items-start justify-between gap-2">
      <div>
        <h1 class="titulo-pagina">Avisos</h1>
        <p class="subtitulo">Las novedades del sistema. Tocá uno para ir a lo que avisa.</p>
      </div>
      <button id="limpiar" class="btn-sec hidden">${iconoTacho} Limpiar avisos</button>
    </div>
    <div class="mb-4">${botonAvisosHtml()}</div>
    <div id="lista" class="space-y-3"><div class="tarjeta text-center text-masa-400">Cargando...</div></div>
    <dialog id="dlg"></dialog>`;

  const lista = contenedor.querySelector("#lista") as HTMLElement;
  const btnLimpiar = contenedor.querySelector("#limpiar") as HTMLButtonElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;

  // El boton de las notificaciones va tambien aca, y no solo en el menu ☰:
  // en la computadora ese menu no existe y no habia como activarlas.
  engancharBotonAvisos(contenedor);

  // Estando parado en esta pantalla, un aviso nuevo aparece solo.
  alLlegarAviso(lista, () => {
    if (!dlg.open) void renderAvisos(contenedor);
  });

  let r: AvisosRespuesta;
  try {
    r = await api.get<AvisosRespuesta>("/api/notificaciones?todos=1");
  } catch (e) {
    lista.innerHTML = `<div class="tarjeta text-red-700">${esc(
      e instanceof Error ? e.message : "Error",
    )}</div>`;
    return;
  }

  const VACIO = `<div class="tarjeta text-center text-masa-400">
       Todavia no tenés avisos. Acá van a aparecer cuando haya novedades de tus pedidos.
     </div>`;

  lista.innerHTML = r.avisos.map(fila).join("") || VACIO;
  btnLimpiar.classList.toggle("hidden", r.avisos.length === 0);

  lista.addEventListener("click", (ev) => {
    const ir = (ev.target as HTMLElement).closest("[data-ir]");
    if (ir) navegar(ir.getAttribute("data-ir") as string);
  });

  // Limpiar: los avisos son novedades, no historial. Lo que origino cada uno
  // (el pedido) sigue estando entero en Mis pedidos / Pedidos web.
  btnLimpiar.addEventListener("click", () => {
    dlg.innerHTML = `
      <div class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
        <h2 class="mb-2 text-lg font-bold">Limpiar los avisos</h2>
        <p class="text-sm text-masa-600">
          Se borran los <b>${esc(r.avisos.length)}</b> avisos de esta lista. Los pedidos no se tocan:
          los seguís viendo donde estaban.
        </p>
        <p id="l_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button id="no" class="btn-sec">Dejarlos</button>
          <button id="si" class="btn-peligro">Si, limpiar</button>
        </div>
      </div>`;
    const lErr = dlg.querySelector("#l_err") as HTMLElement;
    dlg.querySelector("#no")?.addEventListener("click", () => dlg.close());
    dlg.querySelector("#si")?.addEventListener("click", async () => {
      try {
        await api.del("/api/notificaciones");
        dlg.close();
        lista.innerHTML = VACIO;
        btnLimpiar.classList.add("hidden");
        // Por si quedaba alguno sin leer: el numero de la campana se apaga.
        await refrescarContador();
      } catch (e) {
        lErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlg.showModal();
  });

  // Verlos es haberlos leido: se apaga el numero de la barra.
  if (r.pendientes > 0) {
    try {
      await api.post("/api/notificaciones");
      await refrescarContador();
    } catch {
      /* si falla, el numero sigue prendido y se reintenta la proxima vez */
    }
  }
}
